import { Divider, Flex, Text, VStack } from '@chakra-ui/react'
import { UsePracticeContractsStatus } from '@hb/react'
import { useState } from 'react'
import { ElectronicConsentPreview } from '../../Users/Profile/ConsentForms/ElectronicConsent/ElectronicConsentModal'
import { DocumentViewModal } from '../DocumentView/DocumentViewModal'
import { PracticeContractAgreementProvider } from './PracticeContractAgreementProvider'
import { PracticeContractStatusView } from './PracticeContractStatusView'

const PracticeContractsList = ({
  status,
  adminView,
  getSignerLabel,
}: {
  status: UsePracticeContractsStatus
  adminView?: boolean
  getSignerLabel?: (uid: string) => string | undefined
}) => {
  const {
    contracts,
    selectedContract,
    handleViewContract,
    handleCloseModal,
    onSignContract,
    canDownload,
  } = status

  return (
    <Flex bg="white" flexDir="column" w="100%">
      <VStack spacing={0} alignItems="flex-start" w="100%" divider={<Divider />}>
        {contracts.length ? (
          contracts.map(contract => (
            <PracticeContractStatusView
              key={contract.id}
              contract={contract}
              adminView={adminView}
              signerLabel={getSignerLabel?.(contract.toBeSignedByUser)}
              handleViewContract={handleViewContract}
            />
          ))
        ) : (
          <Text px={4} py={2} bg="white" _dark={{ bg: 'gray.900' }} width="100%" color="gray.600">
            No practice contracts
          </Text>
        )}
      </VStack>
      {selectedContract && (
        <DocumentViewModal
          document={selectedContract}
          adminView={adminView}
          onFormSubmit={onSignContract}
          value={selectedContract.text ?? []}
          version={selectedContract.editorVersion ?? 'v2'}
          isOpen={!!selectedContract}
          onClose={handleCloseModal}
          withDownload={canDownload}
        />
      )}
    </Flex>
  )
}

// Provider-facing signing list (apps/providers-app). Wrapped in the practice-contract
// electronic-signature agreement gate: the provider must sign the agreement (shown in the
// preview) before the DocumentViewModal will let them sign an individual contract.
export const PracticeContractsStatusProviderView = ({
  status,
  signerId,
  practiceId,
}: {
  status: UsePracticeContractsStatus
  signerId: string | null
  practiceId: string | null
}) => {
  const [agreementModalOpen, setAgreementModalOpen] = useState(false)
  return (
    <PracticeContractAgreementProvider
      signerId={signerId}
      practiceId={practiceId}
      modalOpen={agreementModalOpen}
      setModalOpen={setAgreementModalOpen}>
      <Flex flexDir="column" w="100%" bg="white" _dark={{ bg: 'gray.900' }}>
        <ElectronicConsentPreview openModal={() => setAgreementModalOpen(true)} />
        <Divider />
        <PracticeContractsList status={status} />
      </Flex>
    </PracticeContractAgreementProvider>
  )
}

// HB super-admin overview for a practice (apps/app). The list is practice-wide, so
// getSignerLabel identifies which provider each contract was sent to.
export const PracticeContractsStatusAdminView = ({
  status,
  getSignerLabel,
}: {
  status: UsePracticeContractsStatus
  getSignerLabel?: (uid: string) => string | undefined
}) => <PracticeContractsList status={status} adminView getSignerLabel={getSignerLabel} />
